import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQuerySearchAddressData = Content.Components['SearchAddressResult'];

interface UseQuerySearchAddressOptions {
  enabled?: boolean;
  initialData?: UseQuerySearchAddressData;
}

interface UseQuerySearchAddressParameters {
  address?: string;
  city?: string;
  countryId?: number;
  countryName?: string;
  postCode?: string;
}

const useQuerySearchAddress = (
  parameters: UseQuerySearchAddressParameters,
  options?: UseQuerySearchAddressOptions,
) => {
  const locale = getLocale();

  const query = useQuery<UseQuerySearchAddressData>(
    [QueryId.SEARCH_ADDRESS, parameters, locale],
    async () => {
      const baseUrl = '/api/addresses/search-address';
      const searchParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQuerySearchAddress', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: options?.enabled,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQuerySearchAddress;
